import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, Sparkles, Crown, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SubscriptionSuccess = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed, expired
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;
  const pollInterval = 2000;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/subscriptions/checkout/status/${sessionId}`,
        { withCredentials: true }
      );

      setPaymentDetails(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        // Trigger confetti celebration!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        // Refresh user data
        try {
          const userResponse = await axios.get(`${API_URL}/auth/me`, { withCredentials: true });
          if (setUser) {
            setUser(userResponse.data.user);
          }
        } catch (e) {
          console.log('Could not refresh user data');
        }
        
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling if still pending
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4" data-testid="subscription-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg"
      >
        <Card className="p-8 rounded-3xl border border-white/20 shadow-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-md text-center">
          {status === 'checking' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                Processing Your Payment
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Please wait while we confirm your subscription...
              </p>
              <div className="flex justify-center gap-1">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-2 h-2 bg-primary rounded-full"
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity }}
                  />
                ))}
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", bounce: 0.5 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center"
              >
                <Check className="w-10 h-10 text-green-500" />
              </motion.div>
              <h1 className="text-3xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                Welcome to Premium! 🎉
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your subscription is now active. Enjoy all the premium features!
              </p>
              
              {paymentDetails && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4 mb-6 text-left">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-500" />
                    Payment Details
                  </h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Amount</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Status</span>
                      <span className="font-semibold text-green-500">Paid</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Link to="/premium" className="block">
                  <Button className="w-full rounded-full bg-gradient-to-r from-primary to-accent text-white font-bold h-12 hover:scale-105 transition-transform">
                    Explore Premium Features
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="ghost" className="w-full rounded-full font-semibold">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                Payment Not Completed
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                We couldn't confirm your payment. Please try again or contact support if the issue persists.
              </p>
              <div className="space-y-3">
                <Link to="/pricing">
                  <Button className="w-full rounded-full bg-primary text-white font-bold h-12">
                    Try Again
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="ghost" className="w-full rounded-full font-semibold">
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white mb-2">
                Session Expired
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your payment session has expired. Please start a new checkout.
              </p>
              <div className="space-y-3">
                <Link to="/pricing">
                  <Button className="w-full rounded-full bg-primary text-white font-bold h-12">
                    View Plans
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="ghost" className="w-full rounded-full font-semibold">
                    Back to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <Link to="/" className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
            <Sparkles className="w-5 h-5" />
            <span className="font-heading font-bold">Socyy</span>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
